/**
 * axelJob — shared client for AXEL's async-job endpoints.
 *
 * WHY: Research Desk / MT5 Setup / Live Trigger run LLM + web_search server-side.
 * On a CPU-throttled production origin those calls can outrun the gateway's
 * request timeout → raw 504s. The backend now returns a job_id instantly and
 * runs the work in the background; we poll a fast status endpoint until it's
 * done. No single request is long-lived, so the gateway never 504s.
 */
const API_URL = process.env.REACT_APP_BACKEND_URL;

function authHeaders(extra = {}) {
  const token = localStorage.getItem("flexx_access_token");
  const h = { ...extra };
  if (token) h["Authorization"] = `Bearer ${token}`;
  return h;
}

/** Start a job. Returns job_id. Throws a friendly Error on 429/503/etc. */
export async function startAxelJob(path, body) {
  let res;
  try {
    res = await fetch(`${API_URL}${path}`, {
      method: "POST",
      credentials: "include",
      headers: authHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify(body),
    });
  } catch (err) {
    if (/Load failed|Failed to fetch|NetworkError/i.test(err?.message || "")) {
      throw new Error("AXEL request didn't start — connection issue. Please try again.");
    }
    throw err;
  }
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    const detail = err?.detail;
    if (typeof detail === "object" && detail?.wait_seconds) {
      throw new Error(`Rate limit reached. Try again in ${detail.wait_seconds}s.`);
    }
    if ((res.status === 503 || res.status === 400) && typeof detail === "string") {
      throw new Error(detail);
    }
    throw new Error(`AXEL error ${res.status}: ${typeof detail === "string" ? detail : res.statusText}`);
  }
  const data = await res.json();
  if (!data.job_id) throw new Error("AXEL couldn't queue the request. Please try again.");
  return data.job_id;
}

/**
 * Poll a job until done/error. Returns the job result object.
 * Each poll is a fast request — gateway-timeout-proof.
 */
export async function pollAxelJob(jobId, { timeoutMs = 180000, intervalMs = 2500 } = {}) {
  const started = Date.now();
  // small initial delay — most jobs aren't ready in <1s anyway
  await new Promise((r) => setTimeout(r, 1200));
  while (Date.now() - started < timeoutMs) {
    let res;
    try {
      res = await fetch(`${API_URL}/api/axel/job/${jobId}`, {
        credentials: "include",
        headers: authHeaders(),
      });
    } catch {
      await new Promise((r) => setTimeout(r, intervalMs));
      continue; // transient network blip — keep polling
    }
    if (res.ok) {
      const d = await res.json();
      if (d.status === "done") return d.result;
      if (d.status === "error") throw new Error(d.error || "AXEL hit an error. Please try again.");
    }
    await new Promise((r) => setTimeout(r, intervalMs));
  }
  throw new Error("AXEL is taking longer than usual — please try again in a moment.");
}

/** Convenience: start + poll in one call. */
export async function runAxelJob(path, body, pollOpts) {
  const jobId = await startAxelJob(path, body);
  return pollAxelJob(jobId, pollOpts);
}
